/*
=========================================================
* RingOperator Homepage — Dispatch design
=========================================================
*/

import { useEffect } from "react";
import { Helmet } from "react-helmet-async";

import MKBox from "components/MKBox";

import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import PromoBanner from "components/PromoBanner";
import PromoPopup from "components/PromoPopup";

// Shared brand-aware footer (single source of truth across all public pages).
import SiteFooter from "components/SiteFooter";

import { APP_BRAND, APP_VERSION } from "version";
import useSignupUrl from "hooks/useSignupUrl";

import { dispatchCss, brandCream } from "pages/RingOperatorHomepage/theme";

const navRoutes = [
  { name: "features", href: "#features", icon: "" },
  { name: "how it works", href: "#how-it-works", icon: "" },
  { name: "pricing", href: "#pricing", icon: "" },
  { name: "faq", href: "#faq", icon: "" },
  { name: "blog", href: "/blog", icon: "" },
];

import Hero from "pages/RingOperatorHomepage/sections/Hero";
import Ticker from "pages/RingOperatorHomepage/sections/Ticker";
import LiveBoard from "pages/RingOperatorHomepage/sections/LiveBoard";
import Features from "pages/RingOperatorHomepage/sections/Features";
import BusinessPage from "pages/RingOperatorHomepage/sections/BusinessPage";
import Personalization from "pages/RingOperatorHomepage/sections/Personalization";
import HowItWorks from "pages/RingOperatorHomepage/sections/HowItWorks";
import Pricing from "pages/RingOperatorHomepage/sections/Pricing";
import FAQ, { faqs } from "pages/RingOperatorHomepage/sections/FAQ";
import FinalCTA from "pages/RingOperatorHomepage/sections/FinalCTA";

function RingOperatorHomepage() {
  const signupUrl = useSignupUrl();

  // Inject Dispatch fonts/keyframes once on mount.
  useEffect(() => {
    const id = "ro-dispatch-css";
    if (document.getElementById(id)) return;
    const style = document.createElement("style");
    style.id = id;
    style.innerHTML = dispatchCss;
    document.head.appendChild(style);
  }, []);

  // SoftwareApplication schema — mirrors RingFoods. Using SoftwareApplication
  // (instead of Product) avoids Google's merchant-listing validator, which
  // requires fields like image and per-offer availability/url that don't
  // naturally apply to a SaaS subscription.
  const schema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "@id": "https://www.ringoperator.com/#software",
    name: "RingOperator AI Phone Agent",
    applicationCategory: "BusinessApplication",
    applicationSubCategory: "Service Business Phone Software",
    operatingSystem: "Web-based",
    description:
      "AI phone agent that answers, books, and qualifies every call — in any language, 24/7 — so small businesses never miss a lead.",
    url: "https://www.ringoperator.com",
    image: "https://www.ringoperator.com/og-image.png",
    offers: [
      {
        "@type": "Offer",
        name: "Starter",
        price: "25",
        priceCurrency: "USD",
        description:
          "Starter plan with included call minutes for very small service operators.",
        availability: "https://schema.org/InStock",
        url: "https://www.ringoperator.com/#pricing",
        image: "https://www.ringoperator.com/og-image.png",
      },
      {
        "@type": "Offer",
        name: "Growth",
        price: "100",
        priceCurrency: "USD",
        description:
          "Growth plan with expanded call minutes and analytics for established service businesses.",
        availability: "https://schema.org/InStock",
        url: "https://www.ringoperator.com/#pricing",
        image: "https://www.ringoperator.com/og-image.png",
      },
      {
        "@type": "Offer",
        name: "Scale",
        price: "300",
        priceCurrency: "USD",
        description:
          "Scale plan with high-volume call minutes and priority support for multi-location operators.",
        availability: "https://schema.org/InStock",
        url: "https://www.ringoperator.com/#pricing",
        image: "https://www.ringoperator.com/og-image.png",
      },
    ],
    featureList: [
      "24/7 AI Phone Answering",
      "Multilingual Support",
      "Lead Qualification",
      "Appointment Booking",
      "Real-time Google Calendar Integration",
      "Call Transfer to Human",
      "Customer SMS Confirmations",
      "Call Transcripts & History",
      "Call Recordings & Playback",
      "Customer Feedback Management",
      "Special & Holiday Hours",
      "Automated Cancellation Notification Calls",
      "Free Public Business Webpage with Online Booking",
      "Waitlist & Queue Management with Live Position",
      "Printable QR Code Poster",
      "Spam Call Blocking",
      "Mid-Call SMS Links",
      "Call Quality Scoring",
      "Real-time Analytics Dashboard",
      "Regulatory Compliance (FCC/CRTC/ICO/ACMA)",
    ],
    softwareVersion: APP_VERSION,
    provider: { "@id": "https://www.ringoperator.com/#organization" },
  };

  // Organization schema — declares Ring AI Inc. as the parent company and
  // links to RingFoods via `sameAs` for cross-domain entity association.
  // This is the canonical SEO signal that ringoperator.com and
  // ringfoods.com are both operated by the same company.
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://www.ringoperator.com/#organization",
    name: "RingOperator",
    legalName: "Ring AI Inc.",
    url: "https://www.ringoperator.com",
    logo: "https://www.ringoperator.com/og-image.png",
    description:
      "AI voice assistant for small and service businesses. Answers every call, books appointments, qualifies leads — 24/7.",
    foundingDate: "2024",
    email: "support@ringoperator.com",
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "Customer Support",
      email: "support@ringoperator.com",
      availableLanguage: ["English"],
    },
    parentOrganization: {
      "@type": "Organization",
      name: "Ring AI Inc.",
      url: "https://www.ringoperator.com",
      subOrganization: [
        { "@type": "Organization", name: "RingFoods", url: "https://www.ringfoods.com" },
        { "@type": "Organization", name: "RingOperator", url: "https://www.ringoperator.com" },
      ],
    },
    sameAs: [
      "https://www.ringoperator.com",
      // Sister brand — cross-domain entity link back to RingFoods.
      "https://www.ringfoods.com",
    ],
  };

  // FAQPage, built from the same `faqs` array the FAQ section renders — the
  // eight Q&As are already on the page, they just weren't marked up.
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": "https://www.ringoperator.com/#faq",
    mainEntity: faqs.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": "https://www.ringoperator.com/#website",
    url: "https://www.ringoperator.com",
    name: "RingOperator",
    description:
      "AI Voice Agent for service businesses — answers, books, qualifies, and routes every call 24/7.",
    publisher: { "@id": "https://www.ringoperator.com/#organization" },
  };

  return (
    <>
      <Helmet>
        {/* Same retrofit as the RingFoods homepage: the <title> now leads with
            the searched-for category ("AI voice agent", "receptionist") instead
            of the positioning line, which contained none of the phrases the Ads
            account buys. The H1 keeps the positioning line. */}
        <title>
          AI Voice Agent &amp; Receptionist for Small Businesses — $25/mo | RingOperator
        </title>
        <meta
          name="description"
          content="RingOperator is an AI voice agent and AI receptionist for small business — 24/7 answering, booking and lead capture for salons, clinics and trades. From $25/mo."
        />
        <meta
          property="og:title"
          content="AI Voice Agent & Receptionist for Small Businesses — $25/mo | RingOperator"
        />
        <meta
          property="og:description"
          content="RingOperator is an AI voice agent and AI receptionist for small business — 24/7 answering, booking and lead capture for salons, clinics and trades. From $25/mo."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.ringoperator.com/" />
        <meta property="og:image" content="https://www.ringoperator.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="AI Voice Agent & Receptionist for Small Businesses — $25/mo | RingOperator"
        />
        <meta
          name="twitter:description"
          content="RingOperator is an AI voice agent and AI receptionist for small business — 24/7 answering, booking and lead capture for salons, clinics and trades. From $25/mo."
        />
        <meta name="twitter:image" content="https://www.ringoperator.com/og-image.png" />
        {/* www, matching every other canonical on the site and what
            prerender.js rewrites this to at build time. The bare-apex value
            here only ever surfaced in the client-rendered SPA. */}
        <link rel="canonical" href="https://www.ringoperator.com/" />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <PromoBanner />
      <PromoPopup />

      <DefaultNavbar
        brand={APP_BRAND}
        routes={navRoutes}
        action={{
          type: "external",
          route: signupUrl,
          label: "Login",
          color: "dark",
        }}
        sticky
        relative
      />

      <MKBox component="main" sx={{ background: brandCream }}>
        <Hero />
        <Ticker />
        <LiveBoard />
        <Features />
        <BusinessPage />
        <Personalization />
        <HowItWorks />
        <Pricing />
        <FAQ />
        <FinalCTA />
      </MKBox>

      <SiteFooter isHomepage />
    </>
  );
}

export default RingOperatorHomepage;
